import { useCallback } from "react";
import { toast } from "react-toastify";

export const useLandlordMetricsData = (
  filters,
  getWorkspaceMetrics,
  getWorkspaceMetricsFullInfo,
  getLandlordActivityMetrics,
  getLandlordFunnelMetrics,
  setSummaryMetrics,
  setLandlordMetrics,
  setTopLandlords,
  abortControllerRef,
  setLoadingSummary,
  setLoadingTable
) => {
  const aggregateByLandlord = useCallback((metrics) => {
    const grouped = {};

    metrics.forEach((metric) => {
      const landlordId = metric.landlord_id?._id;
      const tenantId = metric.tenant_id?._id;
      const workspaceId = metric.workspaceId?._id;

      if (!landlordId) return;

      // Group by landlord only (not landlord-workspace combination)
      const key = landlordId;

      if (!grouped[key]) {
        grouped[key] = {
          landlord: metric.landlord_id,
          tenantIds: new Set(),
          deletedTenantCount: 0,
          workspaces: new Map(), // Store workspace ID -> workspace object
          firstActivity: metric.created_at,
          lastActivity: metric.created_at,
          type: metric.landlord_type || 'N/A',
          tenantChecks: 0,
        };
      }

      // Track workspaces this landlord has activity in (store full workspace object)
      if (workspaceId && metric.workspaceId) {
        grouped[key].workspaces.set(workspaceId, metric.workspaceId);
      }

      // Count tenants and deleted records
      if (metric.deleted === true) {
        grouped[key].deletedTenantCount += 1;
      } else if (tenantId) {
        grouped[key].tenantIds.add(tenantId);
      }else{
        grouped[key].deletedTenantCount += 1;
      }

      // Add tenant checks count
      if (typeof metric.tenant_checks === 'number') {
        grouped[key].tenantChecks += metric.tenant_checks;
      } else if (metric.tenant_checks === true || metric.tenant_check === true) {
        grouped[key].tenantChecks += 1;
      }

      // Update date range
      if (metric.created_at < grouped[key].firstActivity) {
        grouped[key].firstActivity = metric.created_at;
      }
      if (metric.created_at > grouped[key].lastActivity) {
        grouped[key].lastActivity = metric.created_at;
      }
    });

    // Convert to array with tenant count for each landlord
    return Object.values(grouped).map((group) => {
      const tenantCount = group.tenantIds.size;
      const deletedCount = group.deletedTenantCount;
      const totalTenants = tenantCount + deletedCount;
      const workspaceArray = Array.from(group.workspaces.values());

      return {
        landlord: group.landlord,
        workspaceId: null, // No longer specific to a single workspace
        workspaceName: workspaceArray.length === 1
          ? workspaceArray[0]?.name || 'Unknown Workspace'
          : `${workspaceArray.length} workspaces`,
        workspaces: workspaceArray,
        tenantCount: totalTenants,
        activeTenantCount: tenantCount,
        deletedTenantCount: deletedCount,
        firstActivity: group.firstActivity,
        lastActivity: group.lastActivity,
        type: group.type,
        tenantChecks: group.tenantChecks,
      };
    });
  }, []);

  const fetchSummaryMetrics = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoadingSummary(true);
    try {
      const [workspaceRes, activityRes, funnelRes] = await Promise.all([
        getWorkspaceMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlordId: filters.landlordId,
          workspaceId: "",
          tenantId: "",
          self_serving: filters.selfServing,
          landlord_type: filters.landlordType,
        }),
        getLandlordActivityMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlord_type: filters.landlordType,
        }),
        getLandlordFunnelMetrics({
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlord_type: filters.landlordType,
        }),
      ]);

      if (
        workspaceRes?.status === "canceled" ||
        activityRes?.status === "canceled" ||
        funnelRes?.status === "canceled"
      ) {
        return;
      }

      let newSummary = {
        totalRegistrations: 0,
        totalTenants: 0,
        totalLandlords: 0,
        avgTenantsPerLandlord: 0,
        mostActiveWorkspace: "N/A",
        // Activity metrics
        avgWAU: 0,
        avgMAU: 0,
        totalTenantsInvited: 0,
        totalTenantsReviewed: 0,
        totalTenantChecksDone: 0,
        totalPropertiesImported: 0,
        totalPropertiesAwarded: 0,
        funnelProfiles: 0,
        funnelActivated: 0,
        funnelAvgTimeDays: 0,
        funnelAvgTimeMilliseconds: 0,
        funnelActivationRate: 0,
        weeklyBreakdown: [],
        monthlyBreakdown: []
      };

      if (workspaceRes && workspaceRes?.success && workspaceRes?.summary) {
        const summary = workspaceRes?.summary;
        const totalLandlordsCount = new Set(
          workspaceRes.data.map(
            (m) =>
              m.landlord_id?._id?.toString() || m.landlord_id?.toString()
          )
        ).size;

        // Find most active workspace
        const workspaceCounts = {};
        workspaceRes.data.forEach((m) => {
          const wsId =
            m.workspaceId?._id?.toString() || m.workspaceId?.toString();
          if (wsId) {
            workspaceCounts[wsId] = (workspaceCounts[wsId] || 0) + 1;
          }
        });
        const mostActiveWsId = Object.entries(workspaceCounts).sort(
          (a, b) => b[1] - a[1]
        )[0]?.[0];
        const mostActiveWorkspace =
          workspaceRes.data.find(
            (m) =>
              (m.workspaceId?._id?.toString() ||
                m.workspaceId?.toString()) === mostActiveWsId
          )?.workspaceId?.name || "N/A";

        newSummary = {
          ...newSummary,
          totalRegistrations: workspaceRes.data?.length || 0,
          totalTenants: summary.unique_tenants || 0,
          totalLandlords: totalLandlordsCount,
          avgTenantsPerLandlord:
            totalLandlordsCount > 0
              ? summary.unique_tenants / totalLandlordsCount
              : 0,
          mostActiveWorkspace,
        };
      }

      if (activityRes && activityRes.success && activityRes.data) {
        newSummary = {
          ...newSummary,
          ...activityRes.data
        };
      }

      if (funnelRes && funnelRes.success && funnelRes.data) {
        newSummary.funnelProfiles = funnelRes.data.landlords_profiles;
        newSummary.funnelActivated = funnelRes.data.landlords_activated;
        newSummary.funnelAvgTimeDays = funnelRes.data.avg_time_to_first_core_action_days;
        newSummary.funnelAvgTimeMilliseconds = funnelRes.data.avg_time_to_first_core_action_ms;
        newSummary.funnelActivationRate = funnelRes.data.activation_rate_percentage;
      }

      setSummaryMetrics(newSummary);
    } catch (err) {
      console.error("Error fetching summary metrics:", err);
      toast.error("Failed to fetch summary metrics");
    } finally {
      setLoadingSummary(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.landlordId,
    filters.landlordType,
    filters.selfServing,
    getWorkspaceMetrics,
    getLandlordActivityMetrics,
    getLandlordFunnelMetrics,
    setSummaryMetrics,
    setLoadingSummary,
  ]);

  const fetchLandlordMetrics = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoadingTable(true);
    try {
      const response = await getWorkspaceMetricsFullInfo(
        {
          startDate: filters.startDate,
          endDate: filters.endDate,
          landlordId: filters.landlordId,
          workspaceId: "",
          tenantId: "",
          self_serving: filters.selfServing,
          landlord_type: filters.landlordType,
          signal: controller.signal
        }
      );

      if (response?.status === "canceled") {
        return;
      }

      if (response && response.success && response.data) {
        const aggregated = aggregateByLandlord(response.data);

        // Sort by tenant count (descending)
        const sorted = aggregated.sort((a, b) => b.tenantCount - a.tenantCount);

        setLandlordMetrics(sorted);

        // Calculate top 5 landlords by tenant count
        const top5 = sorted?.slice(0, 5);
        setTopLandlords(top5);

      } else {
        setLandlordMetrics([]);
        setTopLandlords([]);
      }
    } catch (err) {
      console.error("Error fetching landlord metrics:", err);
      toast.error("Failed to fetch landlord metrics");
      setLandlordMetrics([]);
      setTopLandlords([]);
    } finally {
      setLoadingTable(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.landlordId,
    filters.landlordType,
    filters.selfServing,
    aggregateByLandlord,
    getWorkspaceMetricsFullInfo,
    setLandlordMetrics,
    setTopLandlords,
    setLoadingTable,
  ]);

  return {
    fetchSummaryMetrics,
    fetchLandlordMetrics,
  };
};